<template>
    <div
        class="contact-card"
        :class="{
            'is-selected': isSelected,
            'is-unread': contact.unread,
            'has-trigger-success': broadcastTriggerSuccess,
            'has-trigger-waiting': broadcastTriggerWaiting
        }"
        @click="handleClick"
    >
        <!-- Broadcast Trigger Success Badge -->
        <div v-if="broadcastTriggerSuccess" class="trigger-success-badge" title="Broadcast mesajına cevap verdi!">
            <i class="material-icons tada-animation">celebration</i>
        </div>

        <!-- Broadcast Trigger Waiting Badge -->
        <div v-if="broadcastTriggerWaiting" class="trigger-waiting-badge" title="Cevap bekleniyor...">
            <i class="material-icons hourglass-animation">hourglass_empty</i>
        </div>

        <!-- Header -->
        <div class="contact-card-header">
            <div class="contact-avatar">
                <div
                    class="avatar-circle"
                    :style="{ backgroundColor: getAvatarColor(contact.fullname) }"
                >
                    {{ getAvatarInitials(contact.fullname) }}
                </div>
                <div
                    v-if="platformIcon"
                    class="platform-badge"
                    :title="contact.platform"
                >
                    <WhatsappIcon
                        v-if="isWhatsappPlatform"
                        :color="'#25D366'"
                        class="whatsapp-icon"
                    />
                    <i v-else class="material-icons">{{ platformIcon }}</i>
                </div>
            </div>

            <div class="contact-info">
                <div class="contact-name">
                    {{ contact.fullname || 'Unknown User' }}
                </div>
                <div class="contact-meta">
                    <span v-if="contact.email" class="meta-item">{{ contact.email }}</span>
                    <span v-else-if="contact.phone" class="meta-item">{{ contact.phone }}</span>
                    <span v-else-if="contact.whatsappNumber" class="meta-item">{{ contact.whatsappNumber }}</span>
                </div>
            </div>
        </div>

        <!-- Last Message Preview -->
        <div v-if="lastMessagePreview" class="contact-last-message">
            <span class="message-icon">
                <i class="material-icons">{{ lastMessageIcon }}</i>
            </span>
            <span class="message-text">{{ lastMessagePreview }}</span>
        </div>

        <!-- Footer Badges -->
        <div class="contact-card-footer">
            <div class="footer-left">
                <span v-if="contact.unread" class="badge badge-unread">
                    Okunmadı
                </span>
                <span v-if="contact.vipCustomer" class="badge badge-vip">
                    <i class="material-icons">star</i>
                    VIP
                </span>
                <span v-if="answerStatusBadge" class="badge" :class="answerStatusBadge.class">
                    {{ answerStatusBadge.text }}
                </span>
            </div>
            <div class="footer-right">
                <span v-if="formattedTime" class="time-badge" :title="fullTimeString">
                    {{ formattedTime }}
                </span>
            </div>
        </div>

        <!-- Tags (if any) -->
        <div v-if="visibleTags.length > 0" class="contact-tags">
            <span
                v-for="tag in visibleTags"
                :key="tag._id"
                class="tag-badge"
                :style="{ backgroundColor: tag.color || '#6b7280' }"
            >
                {{ tag.name }}
            </span>
            <span v-if="remainingTagsCount > 0" class="tag-badge tag-more">
                +{{ remainingTagsCount }}
            </span>
        </div>
    </div>
</template>

<script>
import moment from "moment";
import WhatsappIcon from "../../svg/appBreadCrumb/WhatsappIcon.vue";

export default {
    name: 'ContactCard',
    components: {
        WhatsappIcon,
    },
    props: {
        contact: {
            type: Object,
            required: true,
        },
        isSelected: {
            type: Boolean,
            default: false,
        },
        currentTime: {
            type: Number,
            default: () => Date.now(),
        },
        groupValue: {
            type: [String, Boolean, Number],
            default: null,
        },
    },
    computed: {
        isWhatsappPlatform() {
            const whatsappPlatforms = ["whatsapp", "whatsapp-qr", "whatsapp-v2", "whatsapp-business-api", "whatsapp-business-api-supsis"];
            return whatsappPlatforms.includes(this.contact.platform);
        },
        broadcastTriggerSuccess() {
            const trigger = this.contact.lastTriggerState;
            if (!trigger || trigger.type !== 'broadcast') return false;
            return trigger.answered === true;
        },
        broadcastTriggerWaiting() {
            const trigger = this.contact.lastTriggerState;
            if (!trigger || trigger.type !== 'broadcast') return false;
            if (trigger.answered === true) return false;
            if (this.groupValue === 'failed') return false;
            return true;
        },
        platformIcon() {
            const icons = {
                supsis: 'chat',
                email: 'email',
                whatsapp: 'whatsapp',
                "whatsapp-qr": "whatsapp",
                "whatsapp-v2": "whatsapp",
                "whatsapp-business-api": "whatsapp",
                "whatsapp-business-api-supsis": "whatsapp",
                web: 'language',
                mobile: 'phone_android',
                instagram: 'photo_camera',
                telegram: 'send',
                messenger: 'messenger',
                'meta-leads': 'campaign',
                trendyol: 'shopping_bag',
                linkedin: 'business',
                google: 'search',
                calendar: 'event',
            };
            return icons[this.contact.platform] || 'person';
        },
        lastMessagePreview() {
            const msg = this.contact.lastMessage;
            if (!msg) return null;

            const description = msg.description || '';
            if (description.length > 60) {
                return description.substring(0, 60) + '...';
            }
            return description;
        },
        lastMessageIcon() {
            const msg = this.contact.lastMessage;
            if (!msg) return 'chat_bubble_outline';

            const typeIcons = {
                text: 'chat_bubble_outline',
                image: 'image',
                video: 'videocam',
                audio: 'mic',
                file: 'attach_file',
                location: 'place',
            };
            return typeIcons[msg.messageType] || 'chat_bubble_outline';
        },
        answerStatusBadge() {
            if (this.contact.answerStatus === 'answered') {
                return { text: 'Cevaplandı', class: 'badge-answered' };
            } else if (this.contact.answerStatus === 'unanswered') {
                return { text: 'Cevaplanmadı', class: 'badge-unanswered' };
            }
            return null;
        },
        formattedTime() {
            if (!this.contact.lastActionTime) return null;

            const timeMs = new Date(this.contact.lastActionTime).getTime();
            const nowMs = this.currentTime;
            const diffMs = nowMs - timeMs;

            const diffMinutes = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);

            if (diffMinutes < 1) return 'Şimdi';
            if (diffMinutes < 60) return `${diffMinutes}dk`;
            if (diffHours < 24) return `${diffHours}sa`;
            if (diffDays < 7) return `${diffDays}g`;

            return moment(this.contact.lastActionTime).format('DD.MM');
        },
        fullTimeString() {
            if (!this.contact.lastActionTime) return '';
            return moment(this.contact.lastActionTime).format('DD.MM.YYYY HH:mm');
        },
        visibleTags() {
            const tags = this.contact.tags || [];
            return tags.slice(0, 2);
        },
        remainingTagsCount() {
            const tags = this.contact.tags || [];
            return Math.max(0, tags.length - 2);
        },
    },
    methods: {
        handleClick() {
            this.$emit('click', this.contact);
        },
        getAvatarInitials(name) {
            if (!name) return '?';
            const parts = name.trim().split(' ');
            if (parts.length >= 2) {
                return (parts[0][0] + parts[1][0]).toUpperCase();
            }
            return name.substring(0, 2).toUpperCase();
        },
        getAvatarColor(name) {
            if (!name) return '#6b7280';

            const colors = [
                '#ef4444', '#f97316', '#f59e0b', '#10b981',
                '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899'
            ];

            let hash = 0;
            for (let i = 0; i < name.length; i++) {
                hash = name.charCodeAt(i) + ((hash << 5) - hash);
            }

            return colors[Math.abs(hash) % colors.length];
        },
    },
};
</script>

<style lang="scss" scoped>
.contact-card {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    position: relative;

    &:hover {
        border-color: #3b82f6;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        transform: translateY(-1px);
    }

    &.is-selected {
        border-color: #3b82f6;
        background: #eff6ff;
        box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
    }

    &.is-unread {
        background: #fefce8;
        border-color: #fde047;
    }

    &.has-trigger-success {
        border-left: 4px solid #10b981;
        background: linear-gradient(to right, #d1fae520 0%, white 30%);
    }

    &.has-trigger-waiting {
        border-left: 4px solid #f59e0b;
        background: linear-gradient(to right, #fef3c720 0%, white 30%);
    }
}

.contact-card-header {
    display: flex;
    gap: 10px;
    margin-bottom: 8px;
}

.contact-avatar {
    position: relative;
    flex-shrink: 0;
}

.avatar-circle {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 14px;
}

.platform-badge {
    position: absolute;
    bottom: -2px;
    right: -2px;
    background: white;
    border-radius: 50%;
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);

    .material-icons {
        font-size: 12px;
        color: #6b7280;
    }

    .whatsapp-icon {
        width: 14px;
        height: 14px;
    }
}

.contact-info {
    flex: 1;
    min-width: 0;
}

.contact-name {
    font-weight: 600;
    font-size: 14px;
    color: #1f2937;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 2px;
}

.contact-meta {
    font-size: 12px;
    color: #6b7280;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.contact-last-message {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin-bottom: 8px;
    font-size: 12px;
    color: #4b5563;

    .message-icon {
        flex-shrink: 0;

        .material-icons {
            font-size: 14px;
            color: #9ca3af;
        }
    }

    .message-text {
        flex: 1;
        line-height: 1.4;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }
}

.contact-card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-height: 24px;
}

.footer-left {
    display: flex;
    align-items: center;
    gap: 4px;
    flex: 1;
    min-width: 0;
}

.footer-right {
    flex-shrink: 0;
}

.badge {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
    white-space: nowrap;

    .material-icons {
        font-size: 12px;
    }

    &.badge-unread {
        background: #fef3c7;
        color: #92400e;
    }

    &.badge-vip {
        background: #fef3c7;
        color: #92400e;
    }

    &.badge-answered {
        background: #d1fae5;
        color: #065f46;
    }

    &.badge-unanswered {
        background: #fee2e2;
        color: #991b1b;
    }
}

.time-badge {
    font-size: 11px;
    color: #9ca3af;
    white-space: nowrap;
}

.contact-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 8px;
}

.tag-badge {
    display: inline-block;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 500;
    color: white;

    &.tag-more {
        background: #6b7280;
    }
}

.trigger-success-badge {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 28px;
    height: 28px;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.4);
    z-index: 10;

    .material-icons {
        font-size: 18px;
        color: white;
    }
}

.trigger-waiting-badge {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 28px;
    height: 28px;
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(245, 158, 11, 0.4);
    z-index: 10;

    .material-icons {
        font-size: 18px;
        color: white;
    }
}

@keyframes tada {
    0% {
        transform: scale(1) rotate(0deg);
    }
    10%, 20% {
        transform: scale(0.9) rotate(-8deg);
    }
    30%, 50%, 70% {
        transform: scale(1.1) rotate(8deg);
    }
    40%, 60% {
        transform: scale(1.1) rotate(-8deg);
    }
    80%, 100% {
        transform: scale(1) rotate(0deg);
    }
}

@keyframes hourglass-rotate {
    0% {
        transform: rotate(0deg);
    }
    50% {
        transform: rotate(180deg);
    }
    100% {
        transform: rotate(180deg);
    }
}

.tada-animation {
    animation: tada 2s ease-in-out infinite;
}

.hourglass-animation {
    animation: hourglass-rotate 2s ease-in-out infinite;
}
</style>

